import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, PageHeader, Select } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_COLOR   = { Open:'success', 'In Progress':'info', Resolved:'warning', Closed:'gray' };
const PRIORITY_COLOR = { Low:'gray', Medium:'info', High:'warning', Urgent:'danger' };
const STATUSES       = ['Open','In Progress','Resolved','Closed'];

export default function AdminTickets() {
  const [tickets, setTickets]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [replyMsg, setReplyMsg] = useState('');
  const [replying, setReplying] = useState(false);
  const [filter, setFilter]     = useState('Open');
  const threadRef = useRef();

  useEffect(() => { fetchTickets(); }, []);
  useEffect(() => { if (selected) fetchThread(selected.id); }, [selected]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [thread]);

  const fetchTickets = async () => {
    setLoading(true);
    try { const r = await api.get('/tickets'); const sorted = (r.data.data || []).sort((a,b) => new Date(b.lastReplyAt||b.createdAt) - new Date(a.lastReplyAt||a.createdAt)); setTickets(sorted); }
    catch {} finally { setLoading(false); }
  };

  const fetchThread = async (id) => {
    try { const r = await api.get(`/tickets/${id}`); const data = r.data.data;
        if (data && data.replies) {
          data.replies = [...data.replies].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
        }
        setThread(data); fetchTickets(); }
    catch { toast.error('Failed to load thread'); }
  };

  const sendReply = async () => {
    if (!replyMsg.trim()) return;
    setReplying(true);
    try {
      await api.post(`/tickets/${selected.id}/reply`, { message: replyMsg });
      setReplyMsg('');
      fetchThread(selected.id);
    } catch { toast.error('Failed'); }
    finally { setReplying(false); }
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/tickets/${selected.id}/status`, { status });
      toast.success(`Status → ${status}`);
      setThread(prev => ({ ...prev, status }));
      fetchTickets();
    } catch { toast.error('Failed'); }
  };

  const filtered = filter === 'All' ? tickets : tickets.filter(t => t.status === filter);
  const openCount = tickets.filter(t => t.status === 'Open' || t.status === 'In Progress').length;
  const unread    = tickets.filter(t => !t.isReadByAdmin && t.status !== 'Closed').length;

  return (
    <div>
      <PageHeader title="Support Tickets" subtitle={`${openCount} open · ${unread} unread`} />

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, height: 'calc(100vh - 200px)', minHeight: 500 }}>
        {/* ── TICKET LIST ── */}
        <div style={{ display: 'flex', flexDirection: 'column', background: '#111', border: '1px solid rgba(46,204,113,0.1)', borderRadius: 16, overflow: 'hidden' }}>
          {/* Filter tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(46,204,113,0.08)', padding: '0 8px' }}>
            {['All','Open','In Progress','Resolved','Closed'].map(s => (
              <button key={s} onClick={() => setFilter(s)} style={{ padding: '10px 10px', background: 'none', border: 'none', borderBottom: `2px solid ${filter===s?'#2ECC71':'transparent'}`, color: filter===s?'#2ECC71':'#555', fontWeight: filter===s?700:400, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: -1 }}>
                {s}{s==='All'?` (${tickets.length})`:s==='Open'?` (${tickets.filter(t=>t.status==='Open').length})`:''}
              </button>
            ))}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? <div style={{ padding: 32, textAlign: 'center', color: '#555' }}>Loading...</div> :
              filtered.length === 0 ? <div style={{ padding: 32, textAlign: 'center', color: '#555' }}>No tickets</div> :
              filtered.map(t => (
                <div key={t.id} onClick={() => setSelected(t)} style={{ padding: '13px 16px', borderBottom: '1px solid rgba(255,255,255,0.03)', cursor: 'pointer', background: selected?.id===t.id?'rgba(46,204,113,0.08)':!t.isReadByAdmin?'rgba(46,204,113,0.03)':'transparent', borderLeft: `3px solid ${selected?.id===t.id?'#2ECC71':!t.isReadByAdmin&&t.status!=='Closed'?'rgba(46,204,113,0.4)':'transparent'}`, transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (selected?.id!==t.id) e.currentTarget.style.background='rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { if (selected?.id!==t.id) e.currentTarget.style.background=!t.isReadByAdmin?'rgba(46,204,113,0.03)':'transparent'; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontWeight: !t.isReadByAdmin?800:600, fontSize: 13, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{t.subject}</div>
                    {!t.isReadByAdmin && t.status!=='Closed' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ECC71', flexShrink: 0, marginTop: 3 }} />}
                  </div>
                  {t.user && <div style={{ fontSize: 11, color: '#2ECC71', marginBottom: 5 }}>{t.user.name} · {t.user.company}</div>}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 5 }}>
                    <Badge color={STATUS_COLOR[t.status]||'gray'} style={{ fontSize: 10, padding: '2px 7px' }}>{t.status}</Badge>
                    <Badge color={PRIORITY_COLOR[t.priority]||'gray'} style={{ fontSize: 10, padding: '2px 7px' }}>{t.priority}</Badge>
                    <Badge color="gray" style={{ fontSize: 10, padding: '2px 7px' }}>{t.category}</Badge>
                  </div>
                  <div style={{ fontSize: 10, color: '#444' }}>{t.lastReplyAt ? new Date(t.lastReplyAt).toLocaleString() : new Date(t.createdAt).toLocaleString()}</div>
                </div>
              ))
            }
          </div>
        </div>

        {/* ── THREAD VIEW ── */}
        <div style={{ background: '#111', border: '1px solid rgba(46,204,113,0.1)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selected ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#444', gap:12 }}>
              <div style={{ fontSize: 52 }}>🎫</div>
              <div style={{ fontSize: 14 }}>Select a ticket to view the thread</div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(46,204,113,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{thread?.subject || selected.subject}</div>
                    {(thread?.user || selected.user) && (
                      <div style={{ fontSize: 12, color: '#2ECC71', marginBottom: 6 }}>
                        {(thread?.user || selected.user)?.name} · {(thread?.user || selected.user)?.email} · {(thread?.user || selected.user)?.company}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Badge color={STATUS_COLOR[thread?.status||selected.status]}>{thread?.status||selected.status}</Badge>
                      <Badge color={PRIORITY_COLOR[thread?.priority||selected.priority]}>{thread?.priority||selected.priority}</Badge>
                      <Badge color="gray">{thread?.category||selected.category}</Badge>
                    </div>
                  </div>
                  {/* Status changer */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {STATUSES.filter(s => s !== (thread?.status||selected.status)).map(s => (
                      <button key={s} onClick={() => updateStatus(s)} style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${STATUS_COLOR[s]==='success'?'rgba(46,204,113,0.3)':STATUS_COLOR[s]==='info'?'rgba(52,152,219,0.3)':STATUS_COLOR[s]==='warning'?'rgba(243,156,18,0.3)':'rgba(255,255,255,0.1)'}`, background: 'transparent', color: STATUS_COLOR[s]==='success'?'#2ECC71':STATUS_COLOR[s]==='info'?'#3498DB':STATUS_COLOR[s]==='warning'?'#F39C12':'#888', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                        → {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!thread ? <div style={{ textAlign:'center', padding:32, color:'#555' }}>Loading...</div> :
                  (thread.replies || []).map(reply => (
                    <div key={reply.id} style={{ display: 'flex', flexDirection: reply.isAdmin ? 'row-reverse' : 'row', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: reply.isAdmin ? 'rgba(46,204,113,0.15)' : 'rgba(52,152,219,0.15)', border: `2px solid ${reply.isAdmin ? '#2ECC71' : '#3498DB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                        {reply.isAdmin ? '🛡' : (thread.user?.name?.[0] || '?')}
                      </div>
                      <div style={{ maxWidth: '75%' }}>
                        <div style={{ fontSize: 11, color: '#555', marginBottom: 4, textAlign: reply.isAdmin ? 'right' : 'left' }}>
                          {reply.isAdmin ? 'Support Team (MS Discoveries)' : thread.user?.name || 'Client'} · {new Date(reply.createdAt).toLocaleString()}
                        </div>
                        <div style={{ background: reply.isAdmin ? 'rgba(46,204,113,0.08)' : 'rgba(52,152,219,0.08)', border: `1px solid ${reply.isAdmin ? 'rgba(46,204,113,0.2)' : 'rgba(52,152,219,0.2)'}`, borderRadius: reply.isAdmin ? '14px 4px 14px 14px' : '4px 14px 14px 14px', padding: '12px 16px', fontSize: 13, color: '#ccc', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                          {reply.message}
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* Reply box */}
              {(thread?.status !== 'Closed') && (
                <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(46,204,113,0.08)' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <textarea value={replyMsg} onChange={e => setReplyMsg(e.target.value)}
                      onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Reply to client... (Enter to send)"
                      rows={3}
                      style={{ flex:1, padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none', resize:'none', fontFamily:'inherit', lineHeight:1.6 }} />
                    <Button loading={replying} onClick={sendReply} disabled={!replyMsg.trim()} style={{ alignSelf:'flex-end' }}>
                      Send →
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
